import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import { adminPromoListQuery } from '@xdocs/schemas';
import type { AdminPromoListResult, ErrorAlert, PromoCode } from '@xdocs/schemas';
import type { DataTableQuery } from '@xdocs/ui';
import { useWs } from '../../../ws/WsProvider.js';

const DEFAULT_QUERY: DataTableQuery = { page: 1, pageSize: 25, sort: 'createdAt', dir: 'desc' };
export const ADMIN_PROMO_LIST_QUERY_KEY_PREFIX = 'admin-promo-list' as const;

export interface UsePromoListResult {
  rows: readonly PromoCode[];
  total: number | null;
  nextCursor: string | null;
  query: DataTableQuery;
  onQueryChange: (next: DataTableQuery) => void;
  onLoadMore: () => void;
  isLoading: boolean;
  isLoadingMore: boolean;
  isError: boolean;
  loadMoreError: string | undefined;
  onRetry: () => void;
  invalidate: () => void;
}

/**
 * Server-side pagination/sort/search over `admin.promo.list` (F55, E4 §8.3)
 * — same desktop-numbered / mobile-cursor-append bridge as
 * `routes/shares/useSharesList.ts`.
 */
export function usePromoList(): UsePromoListResult {
  const { wsClient, connectionState } = useWs();
  const queryClient = useQueryClient();
  const [query, setQuery] = useState<DataTableQuery>(DEFAULT_QUERY);
  const [rows, setRows] = useState<PromoCode[]>([]);
  const appendModeRef = useRef(false);
  const enabled = connectionState === 'connected';
  const queryKey = [ADMIN_PROMO_LIST_QUERY_KEY_PREFIX, query] as const;

  const result = useQuery<AdminPromoListResult, ErrorAlert>({
    queryKey,
    queryFn: () => requestTyped(wsClient, 'admin.promo.list', adminPromoListQuery.parse(query)),
    enabled,
  });

  useEffect(() => {
    if (!result.data) return;
    setRows((prev) => (appendModeRef.current ? [...prev, ...result.data.rows] : result.data.rows));
  }, [result.data]);

  const onQueryChange = useCallback((next: DataTableQuery) => {
    appendModeRef.current = false;
    setQuery(next);
  }, []);

  const onLoadMore = useCallback(() => {
    appendModeRef.current = true;
    setQuery((q) => ({ ...q, page: q.page + 1 }));
  }, []);

  const onRetry = useCallback(() => void result.refetch(), [result]);
  const invalidate = useCallback(
    () => void queryClient.invalidateQueries({ queryKey: [ADMIN_PROMO_LIST_QUERY_KEY_PREFIX] }),
    [queryClient],
  );

  const total = result.data?.total ?? null;
  const hasMore = total !== null ? rows.length < total : false;
  const isAppendFetch = appendModeRef.current && result.isFetching;

  return {
    rows,
    total,
    nextCursor: hasMore ? 'more' : null,
    query,
    onQueryChange,
    onLoadMore,
    isLoading: result.isLoading && !appendModeRef.current,
    isLoadingMore: isAppendFetch,
    isError: result.isError,
    loadMoreError: appendModeRef.current && result.isError ? result.error?.message : undefined,
    onRetry,
    invalidate,
  };
}
